import type { EChartsOption } from 'echarts';
import type { CallbackDataParams } from 'echarts/types/dist/shared';

import { formatAuraDateLabel, formatAuraMetric } from '@/utils/auraPresentation';

interface HeatmapOptionInput {
    animation: boolean;
    labels: string[];
    yLabels: string[];
    points: Array<[number, number, number]>;
    unit?: string;
}

export function buildAuraHeatmapOption({
    animation,
    labels,
    yLabels,
    points,
    unit,
}: HeatmapOptionInput): EChartsOption {
    const maximum = Math.max(...points.map((point) => point[2]), 1);

    return {
        animation,
        animationDuration: 350,
        animationDurationUpdate: 250,
        tooltip: {
            trigger: 'item',
            formatter: (params: CallbackDataParams) => {
                const value = Array.isArray(params.value) ? (params.value as number[]) : [0, 0, 0];
                const hour = labels[Number(value[0])] || '-';
                const day = formatAuraDateLabel(yLabels[Number(value[1])] || '-');
                return `<strong>${day}, ${hour}</strong><br/>${formatAuraMetric(value[2], unit)}`;
            },
            backgroundColor: '#242B59',
            borderWidth: 0,
            textStyle: { color: '#FFFFFF', fontSize: 12 },
            extraCssText: 'border-radius: 10px; box-shadow: 0 8px 24px rgba(36,43,89,.18);',
        },
        grid: { top: 16, right: 24, bottom: 86, left: 108, containLabel: true },
        xAxis: {
            type: 'category',
            data: labels,
            name: 'Hora do dia',
            nameLocation: 'middle',
            nameGap: 34,
            axisLabel: { color: '#626468', interval: 1 },
            axisLine: { lineStyle: { color: '#D9E0E7' } },
            axisTick: { show: false },
        },
        yAxis: {
            type: 'category',
            data: yLabels,
            axisLabel: { color: '#626468', formatter: formatAuraDateLabel },
            axisLine: { lineStyle: { color: '#D9E0E7' } },
            axisTick: { show: false },
        },
        visualMap: {
            min: 0,
            max: maximum,
            calculable: true,
            orient: 'horizontal',
            left: 'center',
            bottom: 0,
            text: ['Maior volume', 'Menor volume'],
            textStyle: { color: '#626468', fontSize: 11 },
            inRange: {
                color: ['#EFFAF8', '#A7ECE1', '#48DDC3', '#25BFA8', '#242B59'],
            },
        },
        series: [
            {
                type: 'heatmap',
                data: points,
                label: { show: false },
                itemStyle: {
                    borderColor: '#FFFFFF',
                    borderWidth: 3,
                    borderRadius: 5,
                },
                emphasis: {
                    focus: 'self',
                    scale: true,
                    itemStyle: {
                        borderColor: '#242B59',
                        borderWidth: 2,
                        shadowBlur: 16,
                        shadowColor: 'rgba(36,43,89,.24)',
                    },
                },
            },
        ],
    } as EChartsOption;
}
